import { useState, useCallback, useEffect, useMemo } from 'react';
import { preferences } from '../utils/api';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'numisnavigator_preferences';

const getStoredPreferences = (type) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed[`${type}_preferences`] || null;
    }
  } catch (e) {
    console.error('Failed to parse stored preferences:', e);
  }
  return null;
};

export const usePreferences = (type = 'coin_list') => {
  const defaultPreferences = useMemo(() => {
    const storedPrefs = getStoredPreferences(type);
    return {
      hiddenColumns: storedPrefs?.hiddenColumns || [],
      density: storedPrefs?.density || 'standard',
      filters: storedPrefs?.filters || [],
      sortConfig: storedPrefs?.sortConfig || null,
      quickFilter: storedPrefs?.quickFilter || '',
      pinnedColumns: storedPrefs?.pinnedColumns || [],
      columnOrder: storedPrefs?.columnOrder || [],
      columnWidths: storedPrefs?.columnWidths || {}
    };
  }, [type]);

  const [userPreferences, setUserPreferences] = useState(() => {
    const storedPrefs = getStoredPreferences(type);
    return storedPrefs || defaultPreferences;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  // Load preferences when component mounts and auth state changes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    let mounted = true;
    setLoading(true);
    
    const loadPreferences = async () => {
      try {
        const data = await preferences.get();
        const typePrefs = data[`${type}_preferences`] || defaultPreferences;
        
        // Validate and normalize preferences
        const normalizedPrefs = {
          hiddenColumns: Array.isArray(typePrefs.hiddenColumns) ? typePrefs.hiddenColumns : [],
          density: ['compact', 'standard', 'comfortable'].includes(typePrefs.density) 
            ? typePrefs.density 
            : 'standard',
          filters: Array.isArray(typePrefs.filters) ? typePrefs.filters : [],
          sortConfig: typePrefs.sortConfig || null,
          quickFilter: typePrefs.quickFilter || '',
          pinnedColumns: Array.isArray(typePrefs.pinnedColumns) ? typePrefs.pinnedColumns : [],
          columnOrder: Array.isArray(typePrefs.columnOrder) ? typePrefs.columnOrder : [],
          columnWidths: typeof typePrefs.columnWidths === 'object' ? typePrefs.columnWidths : {}
        };

        if (mounted) {
          setUserPreferences(normalizedPrefs);
        }
      } catch (err) {
        console.error('Failed to load preferences:', err);
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPreferences();

    return () => {
      mounted = false;
    };
  }, [type, isAuthenticated, defaultPreferences]);

  // Debounced save function to batch updates
  const debouncedSave = useCallback(
    debounce(async (newPrefs) => {
      try {
        const currentPreferences = await preferences.get();
        const updatedPreferences = {
          ...currentPreferences,
          [`${type}_preferences`]: newPrefs
        };
        
        // Save to localStorage first for instant access on next load
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedPreferences));
        
        await preferences.update(updatedPreferences);
        setError(null);
      } catch (err) {
        console.error('Failed to save preferences:', err);
        setError(err.message);
      }
    }, 1000),
    [type]
  );

  const updatePreferences = useCallback((updater) => {
    const newPrefs = typeof updater === 'function' 
      ? updater(userPreferences)
      : updater;

    // Only update if preferences actually changed
    if (!isEqual(newPrefs, userPreferences)) {
      // Immediately update UI state
      setUserPreferences(newPrefs);
      
      // Debounce only the server save
      // Immediately update localStorage for instant access
      const storedPrefs = localStorage.getItem(STORAGE_KEY);
      const currentPrefs = storedPrefs ? JSON.parse(storedPrefs) : {};
      const updatedPrefs = {
        ...currentPrefs,
        [`${type}_preferences`]: newPrefs
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedPrefs));

      // Use debounced save for all changes
      debouncedSave(newPrefs);
    }
  }, [userPreferences, debouncedSave, type]);

  // Cleanup debounced save on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return {
    preferences: userPreferences,
    updatePreferences,
    loading,
    error
  };
};
